import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit, addDoc, serverTimestamp, updateDoc, writeBatch, onSnapshot } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { 
  FaArrowLeft, 
  FaBuilding, 
  FaExternalLinkAlt, 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaLink,
  FaEnvelope,
  FaClock,
  FaCalendarAlt,
  FaGraduationCap,
  FaCheckCircle,
  FaHome,
  FaChevronRight,
  FaShareAlt,
  FaMoneyBillWave,
  FaUserTie,
  FaUsers,
  FaBookOpen,
  FaBookmark,
  FaBriefcase,
  FaMapMarkerAlt,
  FaIndustry,
  FaWhatsapp
} from 'react-icons/fa';
import JobCard from '../components/jobs/JobCard';
import Header from '../components/Header';
import Footer from '../components/Footer';

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [job, setJob] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applicationDate, setApplicationDate] = useState(null);
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [aboutCompany, setAboutCompany] = useState('');

  const [error, setError] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    if (typeof location === 'string') return location;
    return [location.area, location.city, location.state, location.country]
      .filter(Boolean)
      .join(', ');
  };

  // Format date with time
  const formatDate = (date, includeTime = false) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    };
    
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return d.toLocaleDateString('en-US', options);
  };

  // Format salary
  const formatSalary = (salary) => {
    if (!salary) return 'Not disclosed';
    if (typeof salary === 'string') return salary;
    
    let formatted = '';
    if (salary.min || salary.max) {
      if (salary.min && salary.max) {
        formatted = `₹${Number(salary.min).toLocaleString()} - ₹${Number(salary.max).toLocaleString()}`;
      } else if (salary.min) {
        formatted = `From ₹${Number(salary.min).toLocaleString()}`;
      } else if (salary.max) {
        formatted = `Up to ₹${Number(salary.max).toLocaleString()}`;
      }
      
      if (salary.type) {
        formatted += ` ${salary.type}`;
      } else {
        formatted += ' per month';
      }
      
      return formatted;
    }
    
    return salary;
  };

  // Get job highlights for the header
  const getJobHighlights = () => {
    if (!job) return [];
    
    const highlights = [];
    
    if (job.jobType) {
      highlights.push({
        text: job.jobType,
        icon: <FaBriefcase className="mr-1.5" />
      });
    }
    
    if (job.location) {
      highlights.push({
        text: formatLocation(job.location),
        icon: <FaMapMarkerAlt className="mr-1.5" />
      });
    }
    
    if (job.salary) {
      highlights.push({
        text: formatSalary(job.salary),
        icon: <FaMoneyBillWave className="mr-1.5" />
      });
    }
    
    if (job.experience) {
      highlights.push({
        text: job.experience,
        icon: <FaUserTie className="mr-1.5" />
      });
    }
    
    return highlights;
  };
  
  // Get job metadata for the sidebar
  const getJobMetadata = () => {
    if (!job) return [];
    
    const metadata = [];
    
    if (job.postedAt) {
      metadata.push({
        label: 'Posted',
        value: formatDate(job.postedAt),
        icon: <FaCalendarAlt className="text-blue-500" />
      });
    }
    
    if (job.deadline) {
      metadata.push({
        label: 'Apply Before',
        value: formatDate(job.deadline),
        icon: <FaClock className="text-blue-500" />
      });
    }
    
    if (job.applicationsCount >= 0) {
      metadata.push({
        label: 'Applications',
        value: job.applicationsCount,
        icon: <FaUsers className="text-blue-500" />
      });
    }
    
    if (job.vacancies) {
      metadata.push({
        label: 'Vacancies',
        value: job.vacancies,
        icon: <FaUserTie className="text-blue-500" />
      });
    }
    
    return metadata;
  };
  
  // Get required skills with icons
  const getSkills = () => {
    if (!job || !job.skills || job.skills.length === 0) return [];
    
    return job.skills.map(skill => ({
      name: skill,
      icon: <FaCheckCircle className="text-green-500 mr-2" />
    }));
  };

  // Check application status using local storage
  useEffect(() => {
    if (!currentUser || !jobId) return;
    
    // Check local storage for application status
    const localStatus = localStorage.getItem(`app_status_${currentUser.uid}_${jobId}`);
    const appDate = localStorage.getItem(`app_date_${currentUser.uid}_${jobId}`);
    
    if (localStatus === 'applied') {
      setApplied(true);
      if (appDate) {
        setApplicationDate(new Date(parseInt(appDate)));
      }
    } else {
      setApplied(false);
      setApplicationDate(null);
    }
  }, [jobId, currentUser]);
  
  // Update local storage when applied state changes
  useEffect(() => {
    if (!currentUser || !jobId) return;
    
    if (applied) {
      localStorage.setItem(`app_status_${currentUser.uid}_${jobId}`, 'applied');
      if (applicationDate) {
        localStorage.setItem(`app_date_${currentUser.uid}_${jobId}`, applicationDate.getTime().toString());
      }
    } else {
      localStorage.removeItem(`app_status_${currentUser.uid}_${jobId}`);
      localStorage.removeItem(`app_date_${currentUser.uid}_${jobId}`);
    }
  }, [applied, applicationDate, currentUser, jobId]);

  // Fetch job details
  // Fetch poster details from organizations collection
  const fetchPosterDetails = async (posterId) => {
    if (!posterId) return { name: 'Company', photoURL: null };
    
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', posterId));
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        const posterData = {
          name: orgData.orgName || orgData.name || 'Company',
          photoURL: orgData.logoUrl || orgData.photoURL || null,
          about: orgData.aboutUs || orgData.about || '',
          isOrganization: true
        };
        
        // Update state with poster details
        setCompanyName(posterData.name);
        if (posterData.photoURL) {
          setCompanyLogo(posterData.photoURL);
        }
        if (posterData.about) {
          setAboutCompany(posterData.about);
        }
        
        return posterData;
      }
      return { name: 'Company', photoURL: null, about: '' };
    } catch (error) {
      console.error('Error fetching poster details:', error);
      return { name: 'Company', photoURL: null, about: '' };
    }
  };

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const jobRef = doc(db, 'jobPosts', jobId);
        const jobDoc = await getDoc(jobRef);
        
        if (!jobDoc.exists()) {
          setError('Job not found');
          setLoading(false);
          return;
        }
        
        const jobData = { id: jobDoc.id, ...jobDoc.data() };
        setJob(jobData);
        
        // Fetch poster details if posterId exists
        if (jobData.posterId) {
          await fetchPosterDetails(jobData.posterId);
        } else {
          // Fallback to job data if available
          setCompanyName(jobData.companyName || 'Company');
          if (jobData.companyLogo) {
            setCompanyLogo(jobData.companyLogo);
          }
        }
        await fetchSimilarJobs(jobData);
      } catch (err) {
        console.error('Error fetching job:', err);
        setError('Failed to load job details');
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  // Fetch similar jobs
  const fetchSimilarJobs = async (jobData) => {
    if (!jobData) return;
    
    try {
      let similarJobsData = [];
      
      // First, try to get jobs from the same company
      if (jobData.companyId) {
        try {
          const similarQuery = query(
            collection(db, 'jobPosts'),
            where('status', '==', 'active'),
            where('companyId', '==', jobData.companyId),
            where('__name__', '!=', jobData.id),
            limit(3)
          );
          
          const querySnapshot = await getDocs(similarQuery);
          querySnapshot.forEach((doc) => {
            similarJobsData.push({ id: doc.id, ...doc.data() });
          });
        } catch (companyError) {
          console.error('Error fetching company jobs:', companyError);
          // Continue with empty array
        }
      }
      
      // If not enough company jobs, try to get by category if available
      const hasCategories = Array.isArray(jobData.categories) && jobData.categories.length > 0;
      if (similarJobsData.length < 3 && hasCategories) {
        try {
          const categoriesToUse = jobData.categories.slice(0, 2);
          if (categoriesToUse.length > 0) {
            const additionalQuery = query(
              collection(db, 'jobPosts'),
              where('status', '==', 'active'),
              where('categories', 'array-contains-any', categoriesToUse),
              where('__name__', '!=', jobData.id),
              limit(3 - similarJobsData.length)
            );
            
            const additionalSnapshot = await getDocs(additionalQuery);
            additionalSnapshot.forEach((doc) => {
              if (!similarJobsData.some(job => job.id === doc.id)) {
                similarJobsData.push({ id: doc.id, ...doc.data() });
              }
            });
          }
        } catch (categoryError) {
          console.error('Error fetching jobs by category:', categoryError);
          // Continue with whatever jobs we have
        }
      }
      
      setSimilarJobs(similarJobsData);
    } catch (err) {
      console.error('Error in fetchSimilarJobs:', err);
    }
  };

  const handleApply = async () => {
    if (!currentUser) {
      window.showSignupModal?.(`/jobs/${jobId}`);
      return;
    }
    if (!job) {
      toast.error('Job information not available. Please refresh the page and try again.');
      return;
    }
    
    setApplying(true);
    const now = new Date();
    
    try {
      // Update local state immediately for better UX
      setApplied(true);
      setApplicationDate(now);
      
      // Update local storage
      localStorage.setItem(`app_status_${currentUser.uid}_${jobId}`, 'applied');
      localStorage.setItem(`app_date_${currentUser.uid}_${jobId}`, now.getTime().toString());
      
      // Try to submit application to Firestore (fire and forget)
      try {
        const userProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userProfile = userProfileDoc.data() || {};
        
        const applicationData = {
          jobId,
          jobTitle: job.title || 'Untitled Job',
          applicantId: currentUser.uid,
          applicantName: userProfile.displayName || currentUser.displayName || 'Anonymous',
          status: 'applied',
          appliedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Add any additional fields that might be needed
          ...(job.posterId && { companyId: job.posterId }),
          ...(job.companyName && { companyName: job.companyName }),
          ...(currentUser.email && { applicantEmail: currentUser.email }),
          ...(userProfile.resumeUrl && { resumeUrl: userProfile.resumeUrl }),
          ...(job.location && { jobLocation: job.location }),
          ...(job.jobType && { jobType: job.jobType })
        };
        
        // Fire and forget - don't await this
        addDoc(collection(db, 'applications'), applicationData)
          .then((docRef) => {
            // Update job applications count if needed
            if (job.id) {
              const jobRef = doc(db, 'jobPosts', job.id);
              updateDoc(jobRef, {
                applicationsCount: (job.applicationsCount || 0) + 1,
                updatedAt: serverTimestamp()
              }).catch(console.error);
            }
            
            // Send email notification
            const notificationData = {
              to: currentUser.email,
              message: {
                subject: `Application Submitted: ${job.title || 'Job Application'}`,
                text: `Thank you for applying to ${job.title || 'the position'} at ${job.companyName || 'our company'}.\n\n` +
                      `Job Title: ${job.title || 'Not specified'}\n` +
                      `Company: ${job.companyName || 'Not specified'}\n` +
                      `Application ID: ${docRef.id}\n` +
                      `Date: ${now.toLocaleDateString()}\n\n` +
                      `We will review your application and get back to you soon.\n\n` +
                      `Best regards,\nThe ${job.companyName || 'Hiring'} Team`
              },
              template: {
                name: 'jobApplicationConfirmation',
                data: {
                  jobTitle: job.title || 'the position',
                  companyName: job.companyName || 'the company',
                  applicationId: docRef.id,
                  applicationDate: now.toLocaleDateString(),
                  jobUrl: window.location.href
                }
              }
            };

            // Add CC if contact email is available
            if (job.contactEmail) {
              notificationData.cc = job.contactEmail;
            }

            // Send notification (fire and forget)
            addDoc(collection(db, 'mail'), notificationData).catch(console.error);
          })
          .catch(console.error);
          
      } catch (firestoreError) {
        console.error('Error saving application to Firestore:', firestoreError);
        // Continue even if Firestore fails - the local state is already updated
      }
      
      toast.success('Application submitted successfully!');
      
    } catch (error) {
      console.error('Error in application process:', error);
      // Revert local state on error
      setApplied(false);
      setApplicationDate(null);
      localStorage.removeItem(`app_status_${currentUser.uid}_${jobId}`);
      localStorage.removeItem(`app_date_${currentUser.uid}_${jobId}`);
      
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  // Share job with platform-specific parameters
  const shareJob = (platform) => {
    if (!job) return;
    
    const jobUrl = `${window.location.origin}/jobs/${jobId}`;
    const hashtags = 'jobsearch,careers,nowhiring';
    const shareText = `Check out this ${job.title} position at ${job.companyName} on ASDFlo`;
    const via = 'asdflo';
    
    // Encode the full share text with URL for each platform
    const encodedUrl = encodeURIComponent(jobUrl);
    const encodedText = encodeURIComponent(shareText);
    
    switch (platform) {
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
          '_blank',
          'noopener,noreferrer,width=550,height=450'
        );
        break;
        
      case 'twitter': {
        const tweetText = `${shareText} ${jobUrl} #${hashtags.split(',').join(' #')} @${via}`;
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`,
          '_blank',
          'width=550,height=420,menubar=no,toolbar=no,resizable=yes,scrollbars=yes'
        );
        break;
      }
        
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
          '_blank',
          'width=600,height=500,menubar=no,toolbar=no,resizable=yes,scrollbars=yes'
        );
        break;
        
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${shareText} ${jobUrl}`)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;
        
      case 'link':
        navigator.clipboard.writeText(jobUrl);
        toast.success('Job URL copied to clipboard!');
        setShowShareMenu(false);
        break;
        
      default:
        break;
    }
    
    // Close the menu after sharing
    if (platform !== 'link') {
      setShowShareMenu(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Error Loading Job</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link 
            to="/jobs" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <FaArrowLeft className="mr-1" /> Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  if (!job) return null;

  // Generate meta description
  const metaDescription = `${job.title} at ${job.companyName} in ${formatLocation(job.location)}. ${job.jobType} position${job.salary ? ' with competitive salary' : ''}. Apply now!`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <Helmet>
        <title>{job ? `${job.title} - ${job.companyName} | Autism Jobs | ASDFlo` : 'Autism Job Details | ASDFlo'}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`autism jobs, ${job?.title}, ${job?.companyName}, autism therapist jobs, special education jobs, autism support jobs, aba therapist jobs, autism careers, ${job?.location}, ${job?.jobType}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={job ? `${job.title} - ${job.companyName} | Autism Jobs | ASDFlo` : 'Autism Job Details | ASDFlo'} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={window.location.href} />
        {job?.companyLogo && <meta property="og:image" content={job.companyLogo} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={job ? `${job.title} - ${job.companyName} | Autism Jobs | ASDFlo` : 'Autism Job Details | ASDFlo'} />
        <meta name="twitter:description" content={metaDescription} />
        {job?.companyLogo && <meta name="twitter:image" content={job.companyLogo} />}
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
        
        {/* Structured Data for Job Posting */}
        {job && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "JobPosting",
              "title": job.title,
              "description": job.description,
              "hiringOrganization": {
                "@type": "Organization",
                "name": job.companyName,
                "sameAs": job.companyWebsite || "https://asdflo.com"
              },
              "datePosted": job.postedAt?.toDate().toISOString(),
              "employmentType": job.jobType,
              "jobLocation": {
                "@type": "Place",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": job.location,
                  "addressRegion": job.state || "",
                  "addressCountry": job.country || "India"
                }
              },
              "industry": "Autism Support Services",
              "occupationalCategory": job.category || "Therapy and Support",
              "qualifications": job.requirements || "",
              "responsibilities": job.responsibilities || "",
              "salaryCurrency": job.salaryCurrency || "INR",
              "baseSalary": {
                "@type": "MonetaryAmount",
                "currency": job.salaryCurrency || "INR",
                "value": {
                  "@type": "QuantitativeValue",
                  "value": job.salary || 0,
                  "unitText": "MONTH"
                }
              },
              "workHours": job.workHours || "Full-time"
            })}
          </script>
        )}
      </Helmet>
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 mb-6">
            <Link to="/jobs" className="hover:text-blue-600 transition-colors">
              <span className="flex items-center">
                <FaHome className="mr-2" /> Jobs
              </span>
            </Link>
            <FaChevronRight className="mx-2 text-gray-400 text-xs" />
            <span className="text-gray-400">Job Details</span>
          </nav>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="lg:w-2/3">
              {/* Job Header */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <div className="flex flex-col md:flex-row md:items-start">
                  {/* Company Logo */}
                  <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                    <div className="h-16 w-16 md:h-20 md:w-20 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                      {job.companyLogo ? (
                        <img 
                          src={job.companyLogo} 
                          alt={`${job.companyName} logo`} 
                          className="h-full w-full object-contain p-1"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/default-company.png';
                          }}
                        />
                      ) : (
                        <FaBuilding className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Job Title and Company */}
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">{job.title}</h1>
                    {companyName ? (
                      <div className="flex items-center text-gray-600 dark:text-gray-300 mb-2">
                        {companyLogo ? (
                          <img 
                            src={companyLogo} 
                            alt={`${companyName} logo`} 
                            className="w-6 h-6 rounded-full mr-2 object-cover"
                          />
                        ) : (
                          <FaBuilding className="mr-2" />
                        )}
                        <span>{companyName}</span>
                      </div>
                    ) : job?.companyName && (
                      <div className="flex items-center text-gray-600 dark:text-gray-300 mb-2">
                        <FaBuilding className="mr-2" />
                        <span>{job.companyName}</span>
                      </div>
                    )}
                    
                    {/* Job Highlights */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {getJobHighlights().map((highlight, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-1.5 rounded-full"
                        >
                          {highlight.icon}
                          {highlight.text}
                        </span>
                      ))}
                    </div>
                    
                    {/* Posted and Applications */}
                    <div className="mt-4 flex flex-wrap items-center text-xs text-gray-500">
                      <span className="flex items-center mr-4 mb-1">
                        <FaClock className="mr-1.5" />
                        Posted {formatDate(job.postedAt || job.createdAt, true)}
                      </span>
                      {job.applicationsCount > 0 && (
                        <span className="flex items-center mb-1">
                          <FaUsers className="mr-1.5" />
                          {job.applicationsCount} application{job.applicationsCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Save and Share */}
                  <div className="mt-4 md:mt-0 md:ml-4 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                      <button 
                        className="text-gray-500 hover:text-blue-600 transition-colors"
                        onClick={() => {}}
                      >
                        <FaBookmark className="inline mr-1.5" />
                        <span className="text-sm">Save</span>
                      </button>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <div className="relative">
                        <button 
                          onClick={() => setShowShareMenu(!showShareMenu)}
                          className="text-gray-500 hover:text-blue-600 transition-colors flex items-center"
                          onBlur={() => setTimeout(() => setShowShareMenu(false), 200)}
                        >
                          <FaShareAlt className="inline mr-1.5" />
                          <span className="text-sm">Share</span>
                        </button>
                        {showShareMenu && (
                          <div 
                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button 
                              onClick={() => shareJob('linkedin')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                            >
                              <FaLinkedin className="text-blue-600 mr-2 text-base" /> LinkedIn
                            </button>
                            <button 
                              onClick={() => shareJob('twitter')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                            >
                              <FaTwitter className="text-blue-400 mr-2 text-base" /> Twitter
                            </button>
                            <button 
                              onClick={() => shareJob('facebook')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                            >
                              <FaFacebook className="text-blue-600 mr-2 text-base" /> Facebook
                            </button>
                            <button 
                              onClick={() => shareJob('whatsapp')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                            >
                              <FaWhatsapp className="text-green-500 mr-2 text-base" /> WhatsApp
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            <button 
                              onClick={() => shareJob('link')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                            >
                              <FaLink className="text-gray-500 mr-2 text-base" /> Copy Link
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Apply Button */}
                    <div className="mt-4">
                      {applied ? (
                        <button
                          disabled
                          className="w-full bg-green-100 text-green-700 py-2 px-4 rounded-md text-sm font-medium"
                        >
                          <FaCheckCircle className="inline mr-2" />
                          {applicationDate 
                            ? `Applied on ${formatDate(applicationDate)}` 
                            : 'Application Submitted'}
                        </button>
                      ) : job.applyUrl ? (
                        <a
                          href={job.applyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2.5 px-6 rounded-full text-sm font-medium text-center transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          Apply Now <FaExternalLinkAlt className="inline ml-1.5 text-xs" />
                        </a>
                      ) : (
                        <button
                          onClick={handleApply}
                          disabled={applying || !job}
                          className={`w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2.5 px-6 rounded-full text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
                        >
                          {applying ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Applying...
                            </>
                          ) : 'Apply Now'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Job Description */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h2>
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: job.description || 'No description provided.' }}
                />
                
                {/* Skills & Requirements */}
                {job.skills && job.skills.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Skills & Requirements</h3>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center bg-gray-100 text-gray-800 text-sm px-3 py-1.5 rounded-full"
                        >
                          <FaCheckCircle className="text-green-500 mr-2" />
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Additional Info */}
                {job.additionalInfo && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Information</h3>
                    <div 
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: job.additionalInfo }}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:w-1/3 space-y-6">
              {/* Sticky Container */}
              <div className="sticky top-6 space-y-6">
                
                {/* Job Overview */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Overview</h3>
                  <div className="space-y-4">
                    {getJobMetadata().map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="bg-blue-50 p-2 rounded-lg text-blue-600 mr-3">
                          {item.icon}
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className="text-sm font-medium text-gray-900">
                            {typeof item.value === 'number' 
                              ? item.value.toLocaleString() 
                              : item.value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Company Details */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-50 p-2 rounded-lg text-blue-600 mr-3">
                      <FaBuilding className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Company Details</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {aboutCompany ? (
                      <div 
                        className="prose prose-sm max-w-none text-gray-600"
                        dangerouslySetInnerHTML={{ __html: aboutCompany }}
                      />
                    ) : job.companyDescription ? (
                      <div 
                        className="prose prose-sm max-w-none text-gray-600"
                        dangerouslySetInnerHTML={{ __html: job.companyDescription }}
                      />
                    ) : (
                      <p className="text-sm text-gray-500">No company details available.</p>
                    )}
                    
                    {(job.companyWebsite || job.companySize || job.companyIndustry) && (
                      <div className="space-y-3">
                        {job.companyWebsite && (
                          <div className="flex items-start">
                            <FaExternalLinkAlt className="mt-0.5 mr-2 text-gray-400 flex-shrink-0" />
                            <a 
                              href={job.companyWebsite.startsWith('http') ? job.companyWebsite : `https://${job.companyWebsite}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-sm"
                            >
                              {job.companyWebsite.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        )}
                        
                        {job.companySize && (
                          <div className="flex items-start">
                            <FaUsers className="mt-0.5 mr-2 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{job.companySize} employees</span>
                          </div>
                        )}
                        
                        {job.companyIndustry && (
                          <div className="flex items-start">
                            <FaIndustry className="mt-0.5 mr-2 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{job.companyIndustry}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Similar Jobs */}
          {similarJobs.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Similar Jobs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {similarJobs.map((similarJob) => (
                  <JobCard 
                    key={similarJob.id} 
                    job={similarJob} 
                    onClick={() => navigate(`/jobs/${similarJob.id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default JobDetailsPage;
